import React, { Suspense, useEffect } from "react";
import "@/index.css";
import "@/i18n";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageShell } from "@/components/PageShell";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SUPPORTED_LANGS } from "@/lib/localePath";

import HomePage from "@/pages/HomePage";
import FaithPage from "@/pages/FaithPage";
import FeaturesPage from "@/pages/FeaturesPage";
import FaqPage from "@/pages/FaqPage";
import ComparisonPage from "@/pages/ComparisonPage";
import PrivacyVsLlmPage from "@/pages/PrivacyVsLlmPage";
import KnowledgePage from "@/pages/KnowledgePage";
import KnowledgeArticlePage from "@/pages/KnowledgeArticlePage";
import ChristianPage from "@/pages/ChristianPage";
import AfterAFightPage from "@/pages/AfterAFightPage";
import ReconnectPage from "@/pages/ReconnectPage";
import NewParentsPage from "@/pages/NewParentsPage";
import LongDistancePage from "@/pages/LongDistancePage";
import ForMenPage from "@/pages/ForMenPage";
import CounselingVsDivorcePage from "@/pages/CounselingVsDivorcePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import RoadmapPage from "@/pages/RoadmapPage";
import StoryPage from "@/pages/StoryPage";
import SupportPage from "@/pages/SupportPage";
import ChangelogPage from "@/pages/ChangelogPage";
import StoreAvailabilityPage from "@/pages/StoreAvailabilityPage";
import NotFoundPage from "@/pages/NotFoundPage";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-white/70 font-serif text-xl italic">
    Loading aurora…
  </div>
);

const withShell = (El) => (
  <PageShell>
    <El />
  </PageShell>
);

// Single source of truth for the page tree. Rendered once at the root (English)
// and once under the optional /:lang prefix (other languages).
const PAGES = [
  { index: true, el: HomePage },
  { path: "faith", el: FaithPage },
  { path: "features", el: FeaturesPage },
  { path: "faq", el: FaqPage },
  { path: "best-ai-couples-apps-2026", el: ComparisonPage },
  { path: "privacy-vs-llm", el: PrivacyVsLlmPage },
  { path: "knowledge", el: KnowledgePage },
  { path: "knowledge/:slug", el: KnowledgeArticlePage },
  { path: "christian", el: ChristianPage },
  { path: "after-a-fight", el: AfterAFightPage },
  { path: "reconnect", el: ReconnectPage },
  { path: "new-parents", el: NewParentsPage },
  { path: "long-distance", el: LongDistancePage },
  { path: "for-men", el: ForMenPage },
  { path: "counseling-vs-divorce", el: CounselingVsDivorcePage },
  { path: "privacy", el: PrivacyPolicyPage },
  { path: "roadmap", el: RoadmapPage },
  { path: "story", el: StoryPage },
  { path: "support", el: SupportPage },
  { path: "changelog", el: ChangelogPage },
  { path: "stores", el: StoreAvailabilityPage },
];

// Legacy .html aliases (English only) — kept for backwards compatibility.
const HTML_ALIASES = PAGES.filter((p) => p.path && !p.path.includes(":")).map((p) => ({
  path: `${p.path}.html`,
  el: p.el,
}));

const renderPages = (keyPrefix = "") =>
  PAGES.map((p) =>
    p.index ? (
      <Route key={`${keyPrefix}index`} index element={withShell(p.el)} />
    ) : (
      <Route key={`${keyPrefix}${p.path}`} path={p.path} element={withShell(p.el)} />
    ),
  );

// Layout for the /:lang branch. Validates the locale segment, syncs i18n, and
// redirects /en/* back to the root (English is unprefixed).
const LangLayout = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const valid = SUPPORTED_LANGS.includes(lang) && lang !== "en";

  useEffect(() => {
    if (valid && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, valid, i18n]);

  if (lang === "en") {
    const rest = location.pathname.replace(/^\/en/, "") || "/";
    return <Navigate to={`${rest}${location.search}${location.hash}`} replace />;
  }
  if (!valid) return withShell(NotFoundPage);
  return <Outlet />;
};

// ?lang=xx on an unprefixed path → 301-style client redirect to /xx<path>.
const LegacyLangRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const q = params.get("lang");
  const firstSeg = location.pathname.split("/")[1];
  if (q && SUPPORTED_LANGS.includes(q) && q !== "en" && !SUPPORTED_LANGS.includes(firstSeg)) {
    params.delete("lang");
    const search = params.toString();
    const base = location.pathname === "/" ? "" : location.pathname;
    return (
      <Navigate
        to={`/${q}${base}${search ? `?${search}` : ""}${location.hash}`}
        replace
      />
    );
  }
  return null;
};

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <BrowserRouter>
        <ScrollToTop />
        <LegacyLangRedirect />
        <Routes>
          {/* English at root */}
          {renderPages()}
          {HTML_ALIASES.map((a) => (
            <Route key={a.path} path={a.path} element={withShell(a.el)} />
          ))}
          {/* Other languages under /:lang */}
          <Route path=":lang" element={<LangLayout />}>
            {renderPages("lang-")}
          </Route>
          <Route path="*" element={withShell(NotFoundPage)} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
}

export default App;
